<template>
  <b-card>
    <SaveChangesAlert @keep="keepSelectedTab" @aware="awareHandler" />
    <div class="d-flex align-items-center justify-content-between">
      <h5>
        Selected Zone: <strong>{{ zoneText }}</strong>
      </h5>
      <b-spinner v-if="fetching" variant="primary" />
    </div>
    <b-tabs v-model="selectedTab">
      <b-tab
        v-for="v in versions"
        :key="v.id"
        :title="extractServiceClass(v.service_class)"
        :disabled="servicesPublished[`${v.service_class}`]"
      >
        <ZoneServiceVersion
          :last-version="v"
          :service="v.service_class"
          :zones="zones"
          @next-tab="nextTabHandler(v.service_class)"
        />
      </b-tab>
    </b-tabs>
  </b-card>
</template>

<script>
import { ref, watch } from "@vue/composition-api";
import { BTab, BTabs, BSpinner, BCard } from "bootstrap-vue";
import useRegisterStoreModule from "@/hooks/useRegisterStoreModule";
import zonesStore from "@/views/zones/zoneStoreModule";
import store from "../../store";
import snakeCaseToCamelCase from "@/utils/snake-case-to-camcel-caase";
import ZoneServiceVersion from "./ZoneServiceVersion.vue";
import SaveChangesAlert from "./SaveChangesAlert.vue";

const SCHEDULE = "::schedule";

const TEMP_SETTINGS = {
  minimum_amount: 1.5,
  tax_amount: 0,
  open_meter_amount: 0.41,
  admin_commission: 0.2,
  drop_zones: {
    "Jordan::Amman": "",
  },
  estimates: {
    value: 15,
    value_upper: 10,
    rate: "percentage",
  },
  peak_factors: [],
  time_slicings: [
    {
      from_unit: 0,
      to_unit: 60,
      amount: 0.035,
      is_fixed_slice: false,
    },
    {
      from_unit: 60,
      to_unit: 1000000,
      amount: 0.06,
      is_fixed_slice: null,
    },
  ],
  distance_slicings: [
    {
      from_unit: 0,
      to_unit: 5,
      amount: 0.18,
      is_fixed_slice: false,
    },
    {
      from_unit: 5,
      to_unit: 20,
      amount: 0.2,
      is_fixed_slice: null,
    },
    {
      from_unit: 20,
      to_unit: 1000000,
      amount: 0.25,
      is_fixed_slice: null,
    },
  ],
  arrival: {
    amount_per_minute: 0,
    free_waiting_time: 300,
    maximum_time_threshold: 900,
  },
  upfront_estimates: {
    active: true,
    minimum_distance_threshold: 1,
    distance: {
      rate: "percentage",
      minimum: 1,
      maximum: 11,
    },
    time: {
      rate: "percentage",
      minimum: 1,
      maximum: 1,
    },
  },
  journey_wait_time: [
    {
      from_unit: 0,
      to_unit: 0,
      amount: 0,
      is_fixed_slice: false,
    },
  ],
  service_fee: [],
  session_discount_slices: [],
  time_frame_slices: [
    {
      start_time: "15:00:00",
      end_time: "18:00:00",
      days: [0, 1, 2, 6, 5],
      time_slicings: [
        {
          from_unit: 0,
          to_unit: 1000,
          amount: 0.4,
          is_fixed_slice: false,
        },
      ],
      distance_slicings: [
        {
          from_unit: 0,
          to_unit: 1000,
          amount: 0.5,
          is_fixed_slice: false,
        },
      ],
    },
  ],
};
export default {
  components: {
    BTab,
    BTabs,
    BCard,
    SaveChangesAlert,
    BSpinner,
    ZoneServiceVersion,
  },
  setup(_, ctx) {
    useRegisterStoreModule("zones", zonesStore);
    useRegisterStoreModule("versions", store);

    const extractServiceClass = (service) => {
      const serviceSplit = service.split("::");
      if (service.includes(SCHEDULE)) {
        return `${snakeCaseToCamelCase(serviceSplit[1])}/${snakeCaseToCamelCase(
          serviceSplit[2]
        )}`;
      }

      return snakeCaseToCamelCase(serviceSplit[1]);
    };

    const zoneParam = ctx.root.$route.query.zone;
    const selectedTab = ref(0);
    const versions = ref([]);
    const fetching = ref(false);
    const servicesPublished = ref({});

    const zones = ref({});
    const fetchingZones = ref(false);
    const zonesNames = ref([]);
    const fetchZones = async () => {
      try {
        fetchingZones.value = true;
        const res = await ctx.root.$store.dispatch("zones/fetchZones");
        zonesNames.value = [
          ...new Set(res.map((el) => `${el.country}::${el.city}`)),
        ];
        zonesNames.value.forEach((name) => {
          zones.value[name] = null;
        });
      } catch (error) {
        console.log(error);
      } finally {
        fetchingZones.value = false;
      }
    };

    const services = ref([]);

    const fetchServicesByZone = async () => {
      const servicesArr = await ctx.root.$store.dispatch(
        "zones/fetchZonesServices",
        zoneParam
      );
      services.value = servicesArr.flatMap((service) => [
        { service, version: null },
        { service: `${service}${SCHEDULE}`, version: null },
      ]);
    };

    const fetchVersions = async () => {
      fetching.value = true;
      await fetchZones();
      await fetchServicesByZone();

      const promises = services.value.map(async (service) =>
        ctx.root.$store.dispatch("versions/fetchVersions", {
          selectedZone: zoneParam,
          distinct: true,
          published: true,
          limit: 1,
          selectedService: service.service,
          withDetails: true,
        })
      );

      const data = await Promise.all(promises);
      versions.value = services.value.map((v) => {
        servicesPublished.value[v.service] = false;
        const version = data.find(
          (ver) => ver.length && ver[0].serviceClass === v.service
        );
        if (version) {
          return {
            service_class: version[0].serviceClass,
            ...version[0],
          };
        }

        return {
          service_class: v.service,
          settings: TEMP_SETTINGS,
        };
      });

      fetching.value = false;
    };

    fetchVersions();

    const nextTabHandler = (serviceClass) => {
      if (selectedTab.value === versions.value.length - 1) {
        ctx.root.$router.push("/versions/prepare-pricing");
      } else {
        servicesPublished.value[`${serviceClass}`] = true;
        selectedTab.value += 1;
      }
    };

    const oldTab = ref(0);

    // If the admin changed the tab that means he didn't save the tab
    // Because after submitting a version the tab will be disabled
    watch(selectedTab, (newVal, oldVal) => {
      if (!fetching.value) {
        const publishedServices = Object.values(servicesPublished.value);
        // Treat oldVal as index for the publishedServices boolean array
        if (publishedServices[oldVal] === false && newVal !== oldTab.value) {
          oldTab.value = oldVal;
          ctx.root.$bvModal.show("save-changes-alert");
        }
      }
    });

    const keepSelectedTab = () => {
      selectedTab.value = oldTab.value;
      ctx.root.$bvModal.hide("save-changes-alert");
    };

    const awareHandler = () => {
      oldTab.value = selectedTab.value;
      ctx.root.$bvModal.hide("save-changes-alert");
    };

    return {
      oldTab,
      zones,
      awareHandler,
      fetching,
      services,
      keepSelectedTab,
      versions,
      selectedTab,
      servicesPublished,
      extractServiceClass,
      zoneParam,
      nextTabHandler,
      zoneText: zoneParam.split("::")[1],
    };
  },
};
</script>
